import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ROUTES } from "../../routes";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { enrolMember } from "../../features/training/slice-extensions/training-slice-extension";
import { ProgrammeSummary } from "../../features/training/models/Programme";
import { todayDateOnly } from "../../features/training/utils/formatDateOnly";
import {
  offersPerVisitContract,
  pickPerVisitPaymentType,
} from "../../features/training/utils/perVisitContractOffer";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { selectActivePaymentTypes } from "../../features/payment-types/payment-types-slice";
import {
  fetchActivePerVisitAccount,
  fetchContracts,
} from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchMemberBalance } from "../../features/billing/slice-extensions/billing-slice-extension";
import PerVisitContractField from "./PerVisitContractField";

interface EnrolProgrammeFormProps {
  memberId: string;
  /** The live programmes the member can be enrolled in. */
  programmes: ProgrammeSummary[];
}

/**
 * Enrols the member in a programme. For a licence-class programme an Owner or Admin is
 * also offered the learner's per-visit account, pre-ticked (PD-0019).
 */
const EnrolProgrammeForm: React.FC<EnrolProgrammeFormProps> = ({ memberId, programmes }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canCreateContract = useAppSelector(selectIsOrgOwnerOrAdmin);
  const paymentTypes = useAppSelector(selectActivePaymentTypes);
  const [programmeId, setProgrammeId] = useState("");
  const [perVisitChecked, setPerVisitChecked] = useState(true);
  const [chosenPaymentTypeId, setChosenPaymentTypeId] = useState<string | null>(null);
  const [hasPerVisitAccount, setHasPerVisitAccount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contractFailed, setContractFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!canCreateContract) return;
    dispatch(fetchPaymentTypes({ includeArchived: false }));
    dispatch(fetchActivePerVisitAccount(memberId)).then((result) => {
      if (fetchActivePerVisitAccount.fulfilled.match(result)) setHasPerVisitAccount(result.payload != null);
    });
  }, [dispatch, memberId, canCreateContract]);

  const showOffer = canCreateContract && offersPerVisitContract(programmes.find((p) => p.id === programmeId));
  const paymentTypeId = paymentTypes.some((p) => p.id === chosenPaymentTypeId)
    ? chosenPaymentTypeId
    : pickPerVisitPaymentType(paymentTypes);
  const createContract = showOffer && perVisitChecked && !hasPerVisitAccount && paymentTypeId != null;

  // The member page's billing card lists contracts from the member balance; the payment form from the list.
  const refreshMemberBilling = () => {
    dispatch(fetchMemberBalance(memberId));
    dispatch(fetchContracts({ page: 1, pageSize: 100 }));
  };

  const handleEnrol = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!programmeId) return;
    setBusy(true);
    setError(null);
    setContractFailed(false);
    const request = {
      programmeId,
      enrolledOn: todayDateOnly(),
      createPerVisitContract: createContract,
      perVisitDefaultPaymentTypeId: createContract ? paymentTypeId : null,
    };
    const result = await dispatch(enrolMember({ memberId, request }));
    setBusy(false);
    if (enrolMember.fulfilled.match(result)) {
      setProgrammeId("");
      setPerVisitChecked(true);
      const outcome = result.payload.perVisitContract;
      if (outcome === "created" || outcome === "alreadyActive") setHasPerVisitAccount(true);
      if (outcome === "created") refreshMemberBilling();
      setContractFailed(outcome === "failed");
    } else if (result.payload?.statusCode === 409) {
      setError(t("training.enrolments.alreadyEnrolled"));
    } else {
      setError(result.payload?.message ?? t("training.enrolments.failed"));
    }
  };

  return (
    <>
      <form onSubmit={handleEnrol} className="mt-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label={t("training.enrolments.choose")}
            className="input min-w-0 flex-1"
            value={programmeId}
            onChange={(e) => setProgrammeId(e.target.value)}
            disabled={busy}
          >
            <option value="">{t("training.enrolments.choose")}</option>
            {programmes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-secondary" disabled={busy || !programmeId}>
            <UserPlus size={18} />
            <span>{t("training.enrolments.enrol")}</span>
          </button>
        </div>
        {showOffer && (
          <PerVisitContractField
            checked={perVisitChecked}
            onCheckedChange={setPerVisitChecked}
            alreadyActive={hasPerVisitAccount}
            paymentTypes={paymentTypes}
            paymentTypeId={paymentTypeId}
            onPaymentTypeChange={setChosenPaymentTypeId}
            disabled={busy}
          />
        )}
      </form>

      {contractFailed && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t("training.enrolments.perVisit.failed")}{" "}
          <Link
            to={ROUTES.MEMBER_BILLING.replace(":id", memberId)}
            className="text-primary-40 dark:text-primary-90 hover:underline"
          >
            {t("billing.actions.billingDetails")}
          </Link>
        </p>
      )}
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </>
  );
};

export default EnrolProgrammeForm;
